const CONSENT_STORAGE_KEY = "runmark:analytics-consent";
export const CONSENT_CHANGE_EVENT = "runmark:analytics-consent-change";

export type ConsentDecision = "granted" | "denied";

// Analytics (Firebase Analytics / GA4, Mixpanel) is the only thing on
// runmark.app that sets non-essential storage -- auth and language preference
// both live in localStorage/IndexedDB already, so nothing else needs to check
// this.
//
// Consent is opt-in only where the law requires it (EEA / UK / Switzerland)
// and on by default everywhere else. "Where" is decided twice, on purpose:
//   - Google Consent Mode (see firebase.ts) denies analytics_storage by
//     default for CONSENT_REQUIRED_REGIONS, using Google's own IP geolocation.
//   - The cookie banner and Mixpanel use isConsentRequiredRegion() below, a
//     browser-timezone heuristic, since the client has no IP geolocation.
// When the two disagree (VPN, travel), the stricter one wins for GA4: an EEA
// IP stays cookieless until an explicit "granted", whatever the timezone.

// ISO 3166-1 codes Google Consent Mode's `region` parameter accepts: the EEA
// (EU-27 plus Iceland, Liechtenstein, Norway), EU outermost regions that have
// their own country code, the UK, and Switzerland.
export const CONSENT_REQUIRED_REGIONS = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", "IS", "LI", "NO", "GF", "GP", "MQ", "RE", "YT", "MF",
  "GB", "CH",
];

// Non-"Europe/" zones that are nonetheless in the EEA.
const CONSENT_REQUIRED_EXTRA_TIMEZONES = new Set([
  "Atlantic/Azores",
  "Atlantic/Canary",
  "Atlantic/Faroe",
  "Atlantic/Madeira",
  "Atlantic/Reykjavik",
  "Arctic/Longyearbyen",
  "Indian/Reunion",
  "Indian/Mayotte",
  "America/Cayenne",
  "America/Guadeloupe",
  "America/Martinique",
  "America/Marigot",
]);

/**
 * Best-effort "is this visitor somewhere that requires opt-in consent?" from
 * the browser's timezone. Over-inclusive on purpose (every Europe/* zone,
 * including non-EEA ones): showing the banner to someone who didn't need it
 * costs nothing, missing someone who did is the failure that matters. Fails
 * closed if the timezone can't be read.
 */
export function isConsentRequiredRegion(): boolean {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timeZone) {
      return true;
    }
    return (
      timeZone.startsWith("Europe/") ||
      CONSENT_REQUIRED_EXTRA_TIMEZONES.has(timeZone)
    );
  } catch {
    return true;
  }
}

export function getConsentDecision(): ConsentDecision | null {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  return raw === "granted" || raw === "denied" ? raw : null;
}

/** An explicit decision always wins; otherwise on by default outside consent regions. */
export function hasAnalyticsConsent(): boolean {
  const decision = getConsentDecision();
  if (decision) {
    return decision === "granted";
  }
  return !isConsentRequiredRegion();
}

export function shouldShowConsentBanner(): boolean {
  return getConsentDecision() === null && isConsentRequiredRegion();
}

export function setConsentDecision(decision: ConsentDecision): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(CONSENT_STORAGE_KEY, decision);
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: decision }));
}
